import { useState, useCallback } from 'react';
import { taskRequestApi } from '../services/taskRequestApi';
import { useApiMutation } from '@hooks/useApiMutation';

interface CompleteTaskDialogProps {
  taskId: number;
  onCompleted: () => void;
  onCancel: () => void;
}

export default function CompleteTaskDialog({
  taskId,
  onCompleted,
  onCancel,
}: Readonly<CompleteTaskDialogProps>) {
  const [resultRecordType, setResultRecordType] = useState('');
  const [resultRecordId, setResultRecordId] = useState('');

  const completeMutation = useApiMutation(
    useCallback(async () => {
      await taskRequestApi.completeTaskRequest(taskId, {
        resultRecordType: resultRecordType || undefined,
        resultRecordId: resultRecordId ? Number(resultRecordId) : undefined,
        completedByUserId: 0, // Server will resolve from auth context
        completedByUserName: '',
      });
    }, [taskId, resultRecordType, resultRecordId]),
    { onSuccess: onCompleted, errorMessage: 'Failed to complete task. Please try again.' },
  );

  const handleSubmit = async () => {
    if (completeMutation.loading) return;
    await completeMutation.mutate();
  };

  return (
    <div className="task-dialog-overlay" onClick={onCancel}>
      <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="task-dialog-header">
          <h3>Complete Task</h3>
        </div>
        <div className="task-dialog-body">
          {completeMutation.error && (
            <p className="task-dialog-error">
              {completeMutation.error}
            </p>
          )}

          <div className="task-dialog-field">
            <label htmlFor="complete-task-record-type">Result Record Type</label>
            <select
              id="complete-task-record-type"
              value={resultRecordType}
              onChange={(e) => setResultRecordType(e.target.value)}
            >
              <option value="">None</option>
              <option value="Clinic">Clinic</option>
              <option value="ClinicLocationAccount">Clinic Location Account</option>
            </select>
          </div>

          <div className="task-dialog-field">
            <label htmlFor="complete-task-record-id">Result Record ID</label>
            <input
              id="complete-task-record-id"
              type="number"
              placeholder="Enter record ID (optional)"
              value={resultRecordId}
              onChange={(e) => setResultRecordId(e.target.value)}
              min={0}
            />
          </div>
        </div>
        <div className="task-dialog-footer">
          <button
            type="button"
            className="task-action-btn task-action-btn-secondary"
            onClick={onCancel}
            disabled={completeMutation.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="task-action-btn task-action-btn-success"
            onClick={handleSubmit}
            disabled={completeMutation.loading}
          >
            {completeMutation.loading ? 'Completing...' : 'Complete Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
